/* 中文词典：core 命名空间。见 src/i18n/README.md。 */

import type { TextSpec } from '../text';

const core = {
  // 低层工具的错误信息
  'core.shuffle.indexOutOfRange': [ 'shuffle: 索引越界 (', { type: 'param', name: 'i' }, ', ', { type: 'param', name: 'j' }, ')，长度 ', { type: 'param', name: 'len' } ],
  'core.rng.needPositiveInt': [ 'nextBelow 需要正整数，收到 ', { type: 'param', name: 'n' } ],
  'core.immutable.draftReceived': [
    'deepFreeze 收到了 immer 的 draft —— 应该在传给 core 之前先用 immer 的 current() 物化成普通对象',
  ],

  // 配置校验
  'core.setup.playerCountRange': [ '玩家数必须是 2–5，当前是 ', { type: 'param', name: 'count' } ],
  'core.setup.missingRequiredPack': [ '必需的数据包「', { type: 'param', name: 'pack' }, '」没有启用' ],
  'core.setup.packsMismatch': [ 'enabledPacks 与注册表不一致：配置是 [', { type: 'param', name: 'configured' }, ']，注册表是 [', { type: 'param', name: 'registry' }, ']' ],
  'core.setup.unknownCard': [ '牌堆里有注册表里不存在的牌：', { type: 'param', name: 'title' } ],
  'core.setup.badCount': [ { type: 'param', name: 'title' }, ' 的数量必须是非负整数，当前是 ', { type: 'param', name: 'count' } ],
  'core.setup.tooMany': [ { type: 'param', name: 'title' }, ' 最多放 ', { type: 'param', name: 'max' }, ' 张，当前是 ', { type: 'param', name: 'count' } ],
  'core.setup.badRescue': [ '每人起手的救命牌数必须是非负整数' ],
  'core.setup.badHandSize': [ '每人起手的随机牌数必须是非负整数' ],
  'core.setup.noRescue': [ '注册表里没有任何带 rescue 特征的牌，抽到致命牌就必死' ],
  'core.setup.noLethal': [ '注册表里没有任何带 lethal 特征的牌，游戏永远不会结束' ],
  'core.setup.badLethal': [ '致命牌数量必须是非负整数' ],
  'core.setup.notEnoughLethal': [ '需要 ', { type: 'param', name: 'needed' }, ' 张致命牌，但牌堆里只配了 ', { type: 'param', name: 'have' }, ' 张' ],
  'core.setup.lethalTooFew': [ '致命牌至少要 ', { type: 'param', name: 'min' }, ' 张（人数 − 1），否则最多只能淘汰 ', { type: 'param', name: 'count' }, ' 人，游戏结束不了' ],
  'core.setup.badExtraRescue': [ '放回牌堆的救命牌数必须是非负整数' ],
  'core.setup.notEnoughRescue': [ '每人发 ', { type: 'param', name: 'perPlayer' }, ' 张救命牌需要 ', { type: 'param', name: 'needed' }, ' 张，但牌堆里只配了 ', { type: 'param', name: 'have' }, ' 张' ],
  'core.setup.notEnoughCards': [ '发牌需要 ', { type: 'param', name: 'needed' }, ' 张（', { type: 'param', name: 'players' }, ' 人 × ', { type: 'param', name: 'handSize' }, ' 张），但扣掉起手救命牌和要移出游戏的牌之后只剩 ', { type: 'param', name: 'remaining' }, ' 张；建议把牌堆加到至少 ', { type: 'param', name: 'suggested' }, ' 张' ],
  'core.setup.emptyDrawPile': [ '按这个配置发完牌后抽牌堆是空的，游戏没法进行' ],
  'core.setup.rescueShortfall': [ 'validateConfig 没拦住：救命牌不够发' ],
  'core.setup.handShortfall': [ 'validateConfig 没拦住：随机牌不够发' ],

  // 数据包注册表
  'core.registry.duplicatePack': [ '数据包 ID 重复：', { type: 'param', name: 'id' } ],
  'core.registry.cardPackMismatch': [ '卡牌 ', { type: 'param', name: 'id' }, ' 声明的 pack 是 ', { type: 'param', name: 'declared' }, '，但它出现在 ', { type: 'param', name: 'pack' }, ' 里' ],
  'core.registry.cardIdMismatch': [ '卡牌 ID 与 pack/local 不一致：id=', { type: 'param', name: 'id' }, ', pack=', { type: 'param', name: 'pack' }, ', local=', { type: 'param', name: 'local' } ],
  'core.registry.cardNamespace': [ '卡牌 ID 的命名空间不对：', { type: 'param', name: 'id' }, ' 应该属于 ', { type: 'param', name: 'pack' } ],
  'core.registry.cardIdDuplicate': [ '卡牌 ID 撞名了：', { type: 'param', name: 'id' }, '（不同数据包不能定义同名卡）' ],
  'core.registry.cardCount': [ '卡牌 ', { type: 'param', name: 'id' }, ' 的 defaultCount 必须是非负整数，实际是 ', { type: 'param', name: 'count' } ],
  'core.registry.effectNamespace': [ '效果 ID 的命名空间不对：', { type: 'param', name: 'id' }, ' 应该属于 ', { type: 'param', name: 'pack' } ],
  'core.registry.effectDuplicate': [ '效果 ID 撞名了：', { type: 'param', name: 'id' } ],
  'core.registry.missingEffect': [ '卡牌 ', { type: 'param', name: 'id' }, ' 声明了效果 ', { type: 'param', name: 'effect' }, '，但没有任何数据包提供它的实现' ],
  'core.registry.empty': [ '注册表里一张牌都没有' ],
  'core.registry.unknownCard': [ '注册表里没有这张牌：', { type: 'param', name: 'title' } ],
  'core.registry.missingEffectImpl': [ '没有效果 ', { type: 'param', name: 'id' }, ' 的实现' ],
  'core.houseRules.duplicate': [ '游戏规则 id 重复登记：「', { type: 'param', name: 'id' }, '」—— 一个设置只能有一份事实' ],

  // 牌名回退
  'core.card.unknown': [ '#', { type: 'param', name: 'uid' }, ' ⟨未知⟩' ],

  // options() 里给玩家看的按钮标签
  'core.option.useRescue': [ '用救命牌' ],
  'core.option.giveUp': [ '放弃（送死）' ],
  'core.option.insertTop': [ '最顶上' ],
  'core.option.insertBottom': [ '最底下' ],
  'core.option.insertNth': [ '从底下数第 ', { type: 'param', name: 'n' }, ' 位' ],
  'core.option.insertTo': [ '插到', { type: 'param', name: 'where' } ],
  'core.option.giveCard': [ '交出 ', { type: 'param', name: 'card' } ],
  'core.option.nope': [ '否决：', { type: 'param', name: 'card' } ],
  'core.option.stealNth': [ '抽第 ', { type: 'param', name: 'n' }, ' 张' ],
  'core.option.pickFromDiscard': [ '捞回 ', { type: 'param', name: 'card' } ],
  'core.option.interrupt': [ '打断：', { type: 'param', name: 'card' } ],
  'core.option.closeWindow': [ '关闭窗口（房主）' ],
  'core.option.playSingleTarget': [ '打出 ', { type: 'param', name: 'card' }, ' → ', { type: 'param', name: 'target' } ],
  'core.option.playSingle': [ '打出 ', { type: 'param', name: 'card' } ],
  'core.option.playPair': [ '一对 ', { type: 'param', name: 'card' }, ' → 抢 ', { type: 'param', name: 'target' } ],
  'core.option.playTriple': [ '三张 ', { type: 'param', name: 'card' }, ' → 向 ', { type: 'param', name: 'target' }, ' 要 ', { type: 'param', name: 'named' } ],
  'core.option.playFive': [ '五张不同名 → 捞回 ', { type: 'param', name: 'card' } ],
  'core.option.draw': [ '抽牌结束回合' ],

  // reducer 的拒绝原因（会经网络发给客户端）
  'core.reason.actionMismatch': [ '动作类型不匹配' ],
  'core.reason.gameAlreadyStarted': [ '游戏已经开始了' ],
  'core.reason.alreadyInRoom': [ '你已经在房间里了' ],
  'core.reason.playerNotFound': [ '玩家不存在' ],
  'core.reason.notInRoom': [ '你不在房间里' ],
  'core.reason.cannotLeave': [ '开局之后不能直接退房，请用断线处理' ],
  'core.reason.cannotSetConfig': [ '开局之后不能改游戏规则，要改就重开一局' ],
  'core.reason.hostOnlyConfig': [ '只有房主能改游戏规则' ],
  'core.reason.hostOnlyStart': [ '只有房主能开局' ],
  'core.reason.needTwoPlayers': [ '至少要 2 个玩家才能开局' ],
  'core.reason.configProblems': [ '游戏规则有问题：', { type: 'param', name: 'problems' } ],
  'core.reason.cardNotInHand': [ '这张牌不在你手上' ],
  'core.reason.someCardsNotInHand': [ '有牌不在你手上' ],
  'core.reason.cardNotPlayable': [ '这张牌不能主动打出' ],
  'core.reason.cardNoEffect': [ '这张牌单独打出去没有任何效果' ],
  'core.reason.cardNeedsTarget': [ '这张牌要指定一个目标' ],
  'core.reason.targetMustBeAlive': [ '目标必须是另一名存活玩家' ],
  'core.reason.pairDisabled': [ '两张同名的连击被房主关掉了' ],
  'core.reason.pairMustMatch': [ '两张牌必须同名' ],
  'core.reason.tripleDisabled': [ '三张同名的连击被房主关掉了' ],
  'core.reason.tripleMustMatch': [ '三张牌必须同名' ],
  'core.reason.namedTitleRequired': [ '要指定一个牌名' ],
  'core.reason.fiveDisabled': [ '五张不同名的连击被房主关掉了' ],
  'core.reason.fiveExact': [ '要正好打出 5 张牌' ],
  'core.reason.cardNotInRegistry': [ '有牌不在卡表里' ],
  'core.reason.fiveDistinct': [ '5 张牌必须牌名各不相同' ],
  'core.reason.pickNotInDiscard': [ '要挑的那张牌不在弃牌堆里' ],
  'core.reason.fiveRescueDisabled': [ '房主关掉了「五张不同名可以捞回救命牌」' ],
  'core.reason.notYourArmedPlay': [ '这不是你举着的那一手' ],
  'core.reason.cannotPlayNow': [ '现在不能出牌' ],
  'core.reason.noArmedPlay': [ '没有举着的牌' ],
  'core.reason.notYourArmedCards': [ '这不是你举着的牌' ],
  'core.reason.cannotDrawNow': [ '现在不能抽牌' ],
  'core.reason.drawFailed': [ '抽牌失败' ],
  'core.reason.noResponseWindow': [ '现在没有响应窗口' ],
  'core.reason.onlyStolenCanNope': [ '只有被抽的人能否决' ],
  'core.reason.onlyAskedCanNope': [ '只有被索要的人能否决' ],
  'core.reason.cardCannotInterrupt': [ '这张牌不能用来打断' ],
  'core.reason.youAreOut': [ '你已经出局了' ],
  'core.reason.hostOnlyCloseWindow': [ '只有房主能关闭响应窗口' ],
  'core.reason.noDecision': [ '现在没有待抉择的事情' ],
  'core.reason.notYourDecision': [ '这不是你要做的决定' ],
  'core.reason.decisionKindMismatch': [ '抉择类型对不上' ],
  'core.reason.noRescueCard': [ '你手上没有救命牌' ],
  'core.reason.stealTargetMissing': [ '被抽的人不在牌桌上' ],
  'core.reason.stealIndexRange': [ '要挑第 0 到 ', { type: 'param', name: 'max' }, ' 张' ],
  'core.reason.orderLengthMismatch': [ '报上来的手牌张数对不上' ],
  'core.reason.orderNotPermutation': [ '报上来的手牌顺序不是那一手牌的排列' ],
  'core.reason.thatCardNotInHand': [ '那一张不在手上' ],
  'core.reason.insertIndexRange': [ '插入位置要在 0 到 ', { type: 'param', name: 'max' }, ' 之间' ],
  'core.reason.lethalNotInHand': [ '那张致命牌不在你手上' ],
  'core.reason.mustGiveNamed': [ '只能交出「', { type: 'param', name: 'title' }, '」' ],
  'core.reason.cardNotCandidate': [ '这张牌不在候选里' ],
  'core.reason.cardNotInDiscard': [ '这张牌已经不在弃牌堆里了' ],
  'core.reason.playerNotAtTable': [ '牌桌上没有这个玩家' ],
  'core.reason.positionInteger': [ '位置必须是整数' ],
  'core.reason.noSuchHandIndex': [ '手上没有这一位' ],

  // 游戏规则设置的 section 与条目
  'core.section.opening': [ '开局' ],
  'core.section.lethal': [ '致命牌' ],
  'core.section.advanced': [ '进阶玩法' ],
  'core.section.misc': [ '其他' ],
  'core.rule.openingHandSize': [ '开局手牌数' ],
  'core.rule.openingRescueCount': [ '开局救命牌' ],
  'core.rule.lethalInDeck': [ '牌堆致命牌数' ],
  'core.rule.lethalInDeck.auto': [ '官方在 ', { type: 'param', name: 'players' }, ' 人局放 ', { type: 'param', name: 'count' }, ' 张' ],
  'core.rule.extraRescueInDeck': [ '牌堆救命牌数' ],
  'core.rule.extraRescueInDeck.auto': [ '官方在 ', { type: 'param', name: 'players' }, ' 人局放回 ', { type: 'param', name: 'count' }, ' 张' ],
  'core.rule.twoOfAKind': [ '打出一对' ],
  'core.rule.twoOfAKind.note': [ '并抢夺一张手牌' ],
  'core.rule.threeOfAKind': [ '打出三条' ],
  'core.rule.threeOfAKind.note': [ '并索要一张手牌' ],
  'core.rule.fiveDifferent': [ '打出五张不同' ],
  'core.rule.fiveDifferent.note': [ '进行弃牌堆检索' ],
  'core.rule.fiveDifferentCanTakeDefuse': [ '弃牌堆检索允许检索救命牌' ],
  'core.rule.deadPlayersCanNope': [ '出局玩家仍可打断行动' ],
} as const;

// 静态检查（写在对象字面量之外，避免 TextKey 循环推断）
core satisfies Record<string, TextSpec>;
export default core;
